import { COMMA, ENTER } from '@angular/cdk/keycodes'
import { Component, ElementRef, ErrorHandler, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete'
import { MatChipInputEvent } from '@angular/material/chips'
import { ErrorStateMatcher } from '@angular/material/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  type AddProductCptResult,
  ApiService,
  AppointmentModel,
  isValidProcedureCode,
  type PatientPalRestError,
  type ProcedureSuggestion,
  type ProcedureSuggestionResults,
  ProcedureSuggestionSource,
  ProductModel,
  STATUS as StudyStatus,
  type UpdateProductCptResult,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { defer, Observable, of, Subject } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, finalize, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'

export type CptDialogMode = 'edit' | 'add'

/** Mirrors the routes' `MAX_CODES_PER_REQUEST`, so the form cannot build a request they will reject. */
export const MAX_CPT_CODES = 25

/** How long typing must pause before the dialog searches. */
export const SUGGESTION_DEBOUNCE_MS = 400

/** Mirrors the route's `MIN_PRODUCT_SEARCH_LENGTH`: shorter input matches too much to be worth a request. */
export const MIN_SEARCH_LENGTH = 2

export interface CptDialogData {
  mode: CptDialogMode
  appointment: AppointmentModel
  /** The row being reconciled. Required in edit mode, absent in add mode. */
  product?: ProductModel
}

export interface StudyOption {
  eid: string
  label: string
}

/** A clinic procedure picked in add mode. It saves as one row carrying all of its codes. */
export type PickedProcedure = Required<Pick<ProcedureSuggestion, 'productId' | 'codes' | 'name'>>

/** The route sent only its best `shown` of `total` matches. */
export interface TruncatedMatches {
  shown: number
  total: number
}

/** What the dropdown shows for one query. */
interface SuggestionView {
  options: ProcedureSuggestion[]
  truncated: TruncatedMatches | null
}

const NO_SUGGESTIONS: SuggestionView = { options: [], truncated: null }

interface CptFormControls {
  /** Typed text, or for a moment the suggestion the autocomplete writes when one is picked. */
  cptCodeInput: FormControl<string | ProcedureSuggestion>
  cptCodes: FormControl<string[]>
  studyEid: FormControl<string>
}

/**
 * Serves both CPT actions on the Procedures & Services card:
 *
 * - edit reconciles one row's code list. Every code stays on that row and the
 *   card renders them comma separated on a single line.
 * - add creates new rows, one line per code or per picked clinic procedure, on a chosen accession.
 */
@Component({
  selector: 'mmx-cpt-dialog',
  templateUrl: './cpt-dialog.component.html',
  styleUrls: ['./cpt-dialog.component.scss'],
  standalone: false,
})
export class CptDialogComponent implements OnInit, OnDestroy {
  @ViewChild('codeFilterInput')
    codeFilterInput!: ElementRef<HTMLInputElement>
  @ViewChild(MatAutocompleteTrigger)
    autocompleteTrigger!: MatAutocompleteTrigger

  cptForm!: FormGroup<CptFormControls>
  isLoading = false
  currentCPTCodes: string[] = []
  mode: CptDialogMode
  product?: ProductModel
  appointment: AppointmentModel
  studyOptions: StudyOption[] = []
  separatorKeysCodes: number[] = [ENTER, COMMA]
  filteredCptCode$: Observable<ProcedureSuggestion[]> = of([])
  /** Only a lone option is pre-activated, so Enter never quietly picks one of several procedures. */
  optionCount = 0
  /** True while a lookup is in flight, so the dropdown can say it is searching. */
  isSearching = false
  /** Set when the route sent only its best matches, so the dropdown can ask for more typing. */
  truncatedMatches: TruncatedMatches | null = null
  pickedProcedures: PickedProcedure[] = []
  readonly maxCptCodes = MAX_CPT_CODES

  /**
   * Bound to the chip grid only, so it has an `ngControl` and can report errors. The grid writes its
   * chips' values here on blur, and a picked procedure's chip is valued by its id, so this must not be
   * `cptCodes`.
   */
  readonly chipGridControl = new FormControl<string[]>([], { nonNullable: true })

  /**
   * Codes held by rows this dialog is not editing. Putting one of them on this row
   * would place the same code on two lines, which the route rejects anyway.
   */
  private otherRowCodes: string[] = []
  /** Names learned from suggestions, shown as a code chip's tooltip. */
  private codeNames = new Map<string, string>()
  /** Raw results per query, so returning to text already searched does not ask again. */
  private suggestionsByQuery = new Map<string, ProcedureSuggestionResults>()

  readonly cptErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: (): boolean => {
      const codes = this.cptForm.controls.cptCodes
      const input = this.cptForm.controls.cptCodeInput

      return (codes.invalid && (codes.touched || this.chipGridControl.touched))
        || codes.hasError('maxlength')
        || input.hasError('invalidProcedureCode')
        || input.hasError('duplicateProcedureCode')
        || input.hasError('tooManyProcedureCodes')
    },
  }

  private destroy$ = new Subject<void>()

  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private errorHandler: ErrorHandler,
    public dialogRef: MatDialogRef<CptDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CptDialogData,
  ) {
    this.mode = data.mode
    this.product = data.product
    this.appointment = data.appointment
    this.currentCPTCodes = this.isEditMode
      ? (this.product?.getCPTCodes() || []).map((code) => code.trim().toUpperCase())
      : []
    this.otherRowCodes = this.collectOtherRowCodes()
    this.studyOptions = this.isEditMode ? [] : this.buildStudyOptions()
  }

  get isEditMode(): boolean {
    return this.mode === 'edit'
  }

  get titleKey(): string {
    return this.isEditMode ? 'cpt-dialog.edit-title' : 'cpt-dialog.add-title'
  }

  get showStudyPicker(): boolean {
    return !this.isEditMode && this.studyOptions.length > 1
  }

  /** Chipped codes that do not belong to a picked procedure, which renders as a chip of its own. */
  get looseCodes(): string[] {
    const pickedCodes = new Set(this.pickedProcedures.flatMap((picked) => picked.codes))

    return this.cptForm.controls.cptCodes.value.filter((code) => !pickedCodes.has(code))
  }

  ngOnInit(): void {
    this.initializeForm()
  }

  private initializeForm(): void {
    const studyRequired = this.showStudyPicker
    const defaultStudyEid = this.studyOptions.length === 1 ? this.studyOptions[0].eid : ''

    this.cptForm = this.formBuilder.nonNullable.group({
      cptCodeInput: this.formBuilder.nonNullable.control<string | ProcedureSuggestion>(''),
      cptCodes: this.formBuilder.nonNullable.control(this.currentCPTCodes, [
        Validators.required,
        Validators.maxLength(MAX_CPT_CODES),
      ]),
      studyEid: this.formBuilder.nonNullable.control(
        { value: defaultStudyEid, disabled: !studyRequired },
        studyRequired ? Validators.required : [],
      ),
    })

    this.initializeCodeSuggestions()
  }

  /**
   * Every code on the appointment except the row being edited. In add mode nothing
   * is excluded, because a new line may not duplicate any existing one.
   */
  private collectOtherRowCodes(): string[] {
    const products = this.appointment?.products || []

    return [...new Set(products
      .filter((product) => !this.isEditMode || product.id !== this.product?.id)
      .flatMap((product) => (product.getCPTCodes() || []).map((code) => code.trim().toUpperCase()))
      .filter(Boolean))]
  }

  private buildStudyOptions(): StudyOption[] {
    const studies = this.appointment?.studies || []
    const products = this.appointment?.products || []
    const seen = new Set<string>()

    return studies.reduce<StudyOption[]>((options, study) => {
      const eid = (study.eid || '').trim()

      // Do not pick cancelled accession
      if (!eid || study.status === StudyStatus.CANCELLED || seen.has(eid)) {
        return options
      }
      seen.add(eid)

      const names = (study.products || [])
        .map((id) => products.find((product) => product.id === id)?.name)
        .filter((name): name is string => Boolean(name))

      options.push({ eid, label: names.length > 0 ? `${eid} - ${names.join(', ')}` : eid })
      return options
    }, [])
  }

  private initializeCodeSuggestions(): void {
    this.filteredCptCode$ = this.cptForm.controls.cptCodeInput.valueChanges.pipe(
      startWith(''),
      // A picked option writes the suggestion itself here: that is a selection, not something to search.
      map((value) => typeof value === 'string' ? value.trim().toUpperCase() : ''),
      debounceTime(SUGGESTION_DEBOUNCE_MS),
      distinctUntilChanged(),
      switchMap((value) => this.loadProcedureSuggestions(value)),
      tap(({ options, truncated }) => {
        this.optionCount = options.length
        this.truncatedMatches = truncated
      }),
      map(({ options }) => options),
      takeUntil(this.destroy$),
    )
  }

  private loadProcedureSuggestions(value: string): Observable<SuggestionView> {
    if (value.length < MIN_SEARCH_LENGTH) {
      return of(NO_SUGGESTIONS)
    }

    const cached = this.suggestionsByQuery.get(value)

    if (cached != null) {
      return of(this.toView(cached))
    }

    // Deferred so the flag rises as the request starts. A request cancelled by newer typing lowers it in
    // `finalize` before its replacement raises it again. The empty first emission clears stale options.
    return defer(() => {
      this.isSearching = true

      return this.apiService.searchCptCodeSuggestions(value).pipe(
        tap((results) => {
          this.suggestionsByQuery.set(value, results)

          for (const suggestion of results.suggestions) {
            if (suggestion.name && suggestion.codes.length === 1) {
              this.codeNames.set(suggestion.codes[0], suggestion.name)
            }
          }
        }),
        map((results) => this.toView(results)),
        // The dropdown stays usable with nothing to offer; the failure goes to the app's error handler.
        catchError((error: unknown) => {
          this.errorHandler.handleError(error)
          return of(NO_SUGGESTIONS)
        }),
        finalize(() => {
          this.isSearching = false
        }),
      )
    }).pipe(startWith(NO_SUGGESTIONS))
  }

  private toView(results: ProcedureSuggestionResults): SuggestionView {
    const shown = results.suggestions.length

    return {
      options: this.excludeUnavailable(results.suggestions),
      truncated: results.total > shown ? { shown, total: results.total } : null,
    }
  }

  /**
   * Hides options that could only be refused: any code on another row, and in add mode any code
   * already chipped. In edit mode a clinic procedure stays while it still has a code to add.
   */
  private excludeUnavailable(suggestions: ProcedureSuggestion[]): ProcedureSuggestion[] {
    const selectedCodes = this.cptForm.controls.cptCodes.value

    return suggestions.filter((suggestion) => {
      if (suggestion.codes.some((code) => this.otherRowCodes.includes(code))) {
        return false
      }

      if (this.isEditMode && suggestion.source === ProcedureSuggestionSource.CLINIC) {
        return suggestion.codes.some((code) => !selectedCodes.includes(code))
      }

      return !suggestion.codes.some((code) => selectedCodes.includes(code))
    })
  }

  /** What the input shows for its value: the typed text, or a picked suggestion's code. */
  readonly displayOption = (value: string | ProcedureSuggestion | null): string =>
    typeof value === 'string' ? value : value?.code ?? ''

  sourceLabelKey(suggestion: ProcedureSuggestion): string | null {
    // Editing merges codes into the row and the save picks reuse or copy, so no label is a promise.
    if (this.isEditMode) {
      return null
    }

    if (suggestion.source === ProcedureSuggestionSource.CLINIC) {
      return 'cpt-dialog.existing-procedure'
    }

    if (suggestion.source === ProcedureSuggestionSource.CMS || suggestion.source === ProcedureSuggestionSource.UNKNOWN) {
      return 'cpt-dialog.new-procedure'
    }

    return null
  }

  codeName(code: string): string {
    return this.codeNames.get(code) ?? ''
  }

  addCptCode(rawCode: string): void {
    const code = (rawCode || '').trim().toUpperCase()

    if (!code) {
      return
    }

    const codesControl = this.cptForm.controls.cptCodes
    const inputControl = this.cptForm.controls.cptCodeInput

    if (codesControl.value.length >= MAX_CPT_CODES) {
      inputControl.setErrors({ tooManyProcedureCodes: true })
      return
    }

    if (!isValidProcedureCode(code)) {
      inputControl.setErrors({ invalidProcedureCode: true })
      return
    }

    if (codesControl.value.includes(code) || this.otherRowCodes.includes(code)) {
      inputControl.setErrors({ duplicateProcedureCode: true })
      return
    }

    codesControl.setValue([...codesControl.value, code])
    inputControl.setValue('')
    inputControl.setErrors(null)
  }

  /**
   * Adds a clinic procedure. In add mode it is one chip that saves as one row, refused whole when any
   * of its codes is taken. In edit mode its codes join the row's own, since the row is a code list.
   */
  addClinicProcedure(suggestion: ProcedureSuggestion): void {
    const codes = [...new Set(suggestion.codes.map((code) => code.trim().toUpperCase()).filter(Boolean))]

    if (suggestion.productId == null || codes.length === 0) {
      return
    }

    const codesControl = this.cptForm.controls.cptCodes
    const inputControl = this.cptForm.controls.cptCodeInput
    const selected = codesControl.value
    const toAdd = this.isEditMode ? codes.filter((code) => !selected.includes(code)) : codes

    if (selected.length + toAdd.length > MAX_CPT_CODES) {
      inputControl.setErrors({ tooManyProcedureCodes: true })
      return
    }

    const taken = codes.some((code) => this.otherRowCodes.includes(code))
      || (this.isEditMode ? toAdd.length === 0 : codes.some((code) => selected.includes(code)))

    if (taken) {
      inputControl.setErrors({ duplicateProcedureCode: true })
      return
    }

    if (!this.isEditMode) {
      this.pickedProcedures = [
        ...this.pickedProcedures,
        { productId: suggestion.productId, codes, name: suggestion.name ?? '' },
      ]
    }

    codesControl.setValue([...selected, ...toAdd])
    inputControl.setValue('')
    inputControl.setErrors(null)
  }

  selectSuggestion(suggestion: ProcedureSuggestion): void {
    if (suggestion.source === ProcedureSuggestionSource.CLINIC) {
      this.addClinicProcedure(suggestion)
    } else {
      this.addCptCode(suggestion.code)
    }

    const inputControl = this.cptForm.controls.cptCodeInput
    const errors = inputControl.errors

    // The autocomplete wrote the picked suggestion into the box; a refused pick leaves its code beside the error.
    if (errors != null) {
      inputControl.setValue(suggestion.code)
      inputControl.setErrors(errors)
      return
    }

    if (this.codeFilterInput?.nativeElement) {
      this.codeFilterInput.nativeElement.value = ''
    }
  }

  onChipInputTokenEnd(event: MatChipInputEvent): void {
    // An active option is the autocomplete's to select. With several options and none active, Enter
    // commits what was typed; bailing on `panelOpen` alone made it a dead keystroke.
    if (this.autocompleteTrigger?.activeOption) {
      return
    }

    this.addCptCode(event.value)

    if (!this.cptForm.controls.cptCodeInput.errors) {
      event.chipInput?.clear()
    }
  }

  removeCptCode(code: string): void {
    const codesControl = this.cptForm.controls.cptCodes
    const inputControl = this.cptForm.controls.cptCodeInput

    codesControl.setValue(codesControl.value.filter((c) => c !== code))
    codesControl.markAsTouched()

    // Removing a chip is the fix for hitting the cap, so the error must not outlive it.
    if (inputControl.hasError('tooManyProcedureCodes')) {
      inputControl.setErrors(null)
    }
  }

  removePickedProcedure(productId: string): void {
    const picked = this.pickedProcedures.find((item) => item.productId === productId)

    if (picked == null) {
      return
    }

    this.pickedProcedures = this.pickedProcedures.filter((item) => item.productId !== productId)

    const codesControl = this.cptForm.controls.cptCodes
    const inputControl = this.cptForm.controls.cptCodeInput

    codesControl.setValue(codesControl.value.filter((code) => !picked.codes.includes(code)))
    codesControl.markAsTouched()

    if (inputControl.hasError('tooManyProcedureCodes')) {
      inputControl.setErrors(null)
    }
  }

  // Each option carries its suggestion, so a pick never depends on which list is current when it lands.
  optionSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectSuggestion(event.option.value as ProcedureSuggestion)
  }

  onCancel(): void {
    this.dialogRef.close()
  }

  onSubmit(): void {
    // The button is disabled while saving; the method guards too, so a second submit cannot race the first.
    if (this.isLoading) {
      return
    }

    if (this.cptForm.invalid) {
      this.cptForm.controls.cptCodes.markAsTouched()
      this.cptForm.controls.studyEid.markAsTouched()
      return
    }

    const submittedCodes = this.cptForm.controls.cptCodes.value
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean)

    if (this.isEditMode && this.areSameCodes(submittedCodes, this.currentCPTCodes)) {
      this.dialogRef.close()
      return
    }

    // Narrowed, not defaulted: an empty id would PUT to /products//cpt. Without its row the edit cannot
    // happen, so say so and close rather than leave a Save that does nothing.
    const sourceProduct = this.product
    if (this.isEditMode && !sourceProduct) {
      this.snackBar.open(this.failureMessage, this.dismissLabel, { duration: 3000 })
      this.dialogRef.close()
      return
    }

    this.isLoading = true
    this.dialogRef.disableClose = true

    const request$: Observable<UpdateProductCptResult | AddProductCptResult> = sourceProduct && this.isEditMode
      ? this.apiService.updateAppointmentProductCpt(
        this.appointment.id,
        sourceProduct.id,
        submittedCodes,
      )
      : this.addRequest(submittedCodes)

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isLoading = false
          this.announce(result)
          this.dialogRef.close(result)
        },
        error: (error: PatientPalRestError) => {
          this.isLoading = false
          this.dialogRef.disableClose = false

          // Only the route's own messages are shown. `error.message` is Angular's text for the failed
          // request, URL included, so a failure without one gets the translated fallback and is logged.
          const routeMessage = error.error?.errors?.[0]?.message || error.error?.message

          if (!routeMessage) {
            this.errorHandler.handleError(error)
          }

          // A failed save can still have created procedures, which the route names; like a success that
          // created one, that message stays until dismissed.
          const config = this.createdBeforeFailing(error) ? {} : { duration: 3000 }
          this.snackBar.open(routeMessage || this.failureMessage, this.dismissLabel, config)
        },
      })
  }

  /** Whether the route reports procedures it created before the save failed (`data.createdProductIds`). */
  private createdBeforeFailing(error: PatientPalRestError): boolean {
    const body: unknown = error.error

    if (body == null || typeof body !== 'object' || !('data' in body)) {
      return false
    }

    const data: unknown = body.data

    return data != null && typeof data === 'object' && 'createdProductIds' in data
      && Array.isArray(data.createdProductIds) && data.createdProductIds.length > 0
  }

  /** Picked procedures go by id, each saving as one row; every other chip goes as a code. */
  private addRequest(submittedCodes: string[]): Observable<AddProductCptResult> {
    const studyEid = this.cptForm.getRawValue().studyEid || undefined
    const productIds = this.pickedProcedures.map((picked) => picked.productId)
    const pickedCodes = new Set(this.pickedProcedures.flatMap((picked) => picked.codes))
    const typedCodes = submittedCodes.filter((code) => !pickedCodes.has(code))

    return this.apiService.addAppointmentProductCpt(this.appointment.id, typedCodes, studyEid, productIds)
  }

  /** A created procedure changes the clinic's catalog, so that message stays until dismissed. */
  private announce(result: UpdateProductCptResult | AddProductCptResult): void {
    const message = this.getSuccessMessage(result)
    const created = this.createdProducts(result)

    if (created.length === 0) {
      this.snackBar.open(message, this.dismissLabel, { duration: 3000 })
      return
    }

    const procedures = created
      .map((product) => `${product.getCPTCodes().join(', ')} – ${product.name}`)
      .join('; ')
    const createdMessage = this.translateService.instant('cpt-dialog.new-procedure-added', { procedures })

    this.snackBar.open(`${message} ${createdMessage}`, this.dismissLabel)
  }

  private createdProducts(result: UpdateProductCptResult | AddProductCptResult): ProductModel[] {
    if ('newProducts' in result) {
      return result.newProducts.filter((product) => result.createdProductIds.includes(product.id))
    }

    return result.created ? [result.newProduct] : []
  }

  private get dismissLabel(): string {
    return this.translateService.instant('common.dismiss')
  }

  /** Names the action the user actually attempted, so a POST failure is not reported as an edit. */
  private get failureMessage(): string {
    return this.translateService.instant(
      this.isEditMode ? 'cpt-dialog.update-failed' : 'cpt-dialog.add-failed',
    )
  }

  private getSuccessMessage(result: UpdateProductCptResult | AddProductCptResult): string {
    if (!('newProducts' in result)) {
      return this.translateService.instant('cpt-dialog.updated')
    }

    const added = result.newProducts.length
    const message = added === 0
      ? this.translateService.instant('cpt-dialog.no-codes-added')
      : this.translateService.instant(
        added === 1 ? 'cpt-dialog.added-row' : 'cpt-dialog.added-rows',
        { count: added },
      )
    // A skipped picked procedure is named with its clashing codes, so those are not listed again below.
    const skippedProcedures = result.skippedProducts.map((skipped) => this.translateService.instant(
      skipped.overlappingCodes.length === 1 ? 'cpt-dialog.skipped-procedure' : 'cpt-dialog.skipped-procedure-codes',
      { procedure: `${skipped.name} (${skipped.codes.join(', ')})`, codes: skipped.overlappingCodes.join(', ') },
    ))
    const namedCodes = new Set(result.skippedProducts.flatMap((skipped) => skipped.overlappingCodes))
    const looseCodes = result.skippedCodes.filter((code) => !namedCodes.has(code))
    const alreadyPresent = looseCodes.length > 0
      ? this.translateService.instant('cpt-dialog.already-present', { codes: looseCodes.join(', ') })
      : ''

    return [message, ...skippedProcedures, alreadyPresent].filter(Boolean).join(' ')
  }

  private areSameCodes(left: string[], right: string[]): boolean {
    if (!left || !right || left.length !== right.length) {
      return false
    }

    const leftSorted = [...left].sort()
    const rightSorted = [...right].sort()

    return leftSorted.every((code, index) => code === rightSorted[index])
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
